<template>
    <div class="dashboard px-0 pt-3 pt-sm-0 pb-0">
        <!-- challenges -->
        <v-row v-if="challenges.length > 0">
            <v-col cols="12" class="py-0">
                <div class="challenges-widget mt-2 mt-sm-5 mx-2 mx-sm-0 mb-3 mb-sm-0">
                    <!-- skeleton-loading -->
                    <template v-if="isFetchingLatestNews">
                        <div class="d-flex align-center">
                            <v-skeleton-loader type="avatar" class="mr-3"/>
                            <v-skeleton-loader type="chip" tile/>
                            <v-skeleton-loader type="button" tile class="ml-auto"/>
                        </div>
                    </template>

                    <!-- show accordion of challenges -->
                    <template>
                        <v-expansion-panels tile flat>
                            <v-expansion-panel>
                                <v-expansion-panel-header
                                        color="accent"
                                        class="btn--challenges"
                                        expand-icon="mdi-menu-down"
                                >
                                    <v-icon class="flex-grow-0 mr-3">mdi-medal-outline</v-icon>
                                    <span class="mr-auto font-weight-bold">
                                        {{ $vuetify.lang.t('$vuetify.challenges.active_challenges') }}
                                    </span>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content transparent>
                                    <template v-for="challenge in challenges">
                                        <router-link
                                                :key="challenge.id"
                                                class="d-flex align-center py-3 pr-3 pl-1 body-2 white--text"
                                                :to="{ name: routeAccount, query: { challengeId: challenge.id } }"
                                        >
                                            <v-icon>mdi-chevron-right</v-icon>
                                            <span class="font-weight-bold">
                                                {{ challenge.title }}
                                            </span>
                                            <div class="ml-auto d-flex flex-shrink-0">
                                                {{ $vuetify.lang.t('$vuetify.challenges.achieved_points') }}:
                                                <span class="ml-2 total-bonus-points font-weight-bold">
                                                    {{ challenge.total_bonus_points | number | balance }}
                                                </span>
                                            </div>
                                        </router-link>
                                    </template>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </template>
                </div>
            </v-col>
        </v-row>
        <!-- /challenges -->

        <!-- latest news -->
        <v-row>
            <v-col cols="12" class="py-0">
                <heading-section class="d-none d-sm-flex"
                                 :loading="isFetchingLatestNews"
                                 v-if="!isFetchingLatestNews">
                    {{ $vuetify.lang.t('$vuetify.title.your_news') }}
                </heading-section>
                <v-skeleton-loader type="heading" tile v-else class="d-none d-sm-block my-5"/>
            </v-col>

            <!-- news hero -->
            <v-col cols="12" sm="5" class="pt-0 pb-0">
                <v-container class="pa-0 fill-height-sm">
                    <template v-if="!isFetchingLatestNews">
                        <v-card v-if="heroArticle"
                                :to="{ name: 'news-details', params: { newsId: heroArticle.id }}"
                                flat
                                class="fill-height-sm mb-2 my-sm-0 pa-2"
                                tile>
                            <v-img
                                    v-if="heroArticle.image_preview || heroArticle.image_header"
                                    :src="heroArticle.image_preview ? heroArticle.image_preview.source : heroArticle.image_header.source"
                                    aspect-ratio="1"
                                    class="mb-3"></v-img>
                            <v-row no-gutters>
                                <v-col class="pl-5 pr-5">
                                    <div class="subtitle-2 secondary--text">{{ heroArticle.date_of_news | date }}</div>
                                    <h2 class="mt-0 mb-1 headline font-weight-bold"
                                        id="dashboard-hero-news">
                                        {{ heroArticle.headline }}
                                    </h2>
                                    <p class="mb-0 body-1">{{ heroArticle.teasertext }}</p>
                                </v-col>
                                <v-col cols="auto" class="d-flex align-end flex-column">
                                    <v-img v-for="(brand, index) in heroArticle.related_brands"
                                           :key="index"
                                           class="mb-1"
                                           max-width="50px"
                                           max-height="18px"
                                           contain
                                           :src="brand.image"></v-img>
                                </v-col>
                            </v-row>
                        </v-card>
                    </template>

                    <!-- skeleton loading -->
                    <template v-else>
                        <v-card class="mb-2 mb-sm-0 pa-2" flat tile>
                            <v-skeleton-loader type="image" tile/>
                            <v-skeleton-loader type="image" class="mb-3" tile/>
                            <v-row no-gutters>
                                <v-col class="pl-5 pr-5">
                                    <v-skeleton-loader type="heading" class="mb-2"/>
                                    <v-skeleton-loader type="text" class="mb-1"/>
                                    <v-skeleton-loader type="sentences"/>
                                </v-col>
                            </v-row>
                        </v-card>
                    </template>
                    <!-- /skeleton loading -->
                </v-container>
            </v-col>
            <!-- /news hero -->

            <!-- latest news -->
            <v-col cols="12" sm="7" class="py-0">
                <v-container pa-0 class="fill-height align-content-space-between">
                    <v-row no-gutters>
                        <v-col
                                :key="article.id"
                                class="mb-2"
                                cols="12"
                                v-for="article in latestArticles">

                            <news-card :article="article" v-if="!isFetchingLatestNews"></news-card>

                            <!-- skeleton loading -->
                            <v-card class="fill-height pa-2 mb-2" flat tile v-else>
                                <v-row no-gutters class="align-center">
                                    <v-col cols="4" class="flex-shrink-0">
                                        <v-skeleton-loader type="image" tile flat/>
                                    </v-col>

                                    <v-col class="pt-0 pb-2 pl-3 pr-2">
                                        <v-skeleton-loader type="heading" class="mb-2"/>
                                        <v-skeleton-loader type="sentences" class="mb-3"/>
                                        <v-skeleton-loader type="text@2"/>
                                    </v-col>
                                </v-row>
                            </v-card>
                            <!-- /skeleton loading -->

                        </v-col>
                    </v-row>

                    <!-- all news link -->
                    <v-row no-gutters class="align-center justify-center">
                        <v-col class="px-7 px-sm-0 mb-8 mb-sm-0">
                            <v-btn class="v-btn--block v-btn--outlined btn-ghost"
                                   depressed
                                   :to="toAllNews"
                                   v-if="!isFetchingLatestNews">
                                {{ $vuetify.lang.t('$vuetify.buttons.all_news') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <!-- /all news link -->
                </v-container>
            </v-col>
            <!-- /latest news -->
        </v-row>

        <v-row class="d-none d-sm-flex">
            <v-col class="pa-0">
                <v-divider class="mt-5"></v-divider>
            </v-col>
        </v-row>

        <!-- performance & rang & wishlist -->
        <v-row>
            <v-col cols="12" class="py-0">
                <heading-section class="d-none d-sm-flex" :loading="isFetchingLatestNews">
                    {{ $vuetify.lang.t('$vuetify.title.dashboard_performance_wishlist') }}
                </heading-section>
            </v-col>
            <v-col cols="12" md="5" class="py-0">
                <!-- account performance -->
                <widget-performance/>
                <!-- rang -->
                <widget-rangs/>
            </v-col>
            <v-col cols="12" md="7" class="py-0">
                <!-- wishlist -->
                <widget-wishlist class="mt-md-7"/>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    // utils
    import { setPageTitle } from '@/utils/helper';
    // components
    import HeadingSection from '../components/HeadingSection';
    import NewsCard from '../components/Cards/News';
    import WidgetPerformance from '../components/Widgets/Performance';
    import WidgetWishlist from '../components/Widgets/Wishlist';
    import WidgetRangs from '../components/Widgets/Rangs';
    // models
    import Challenge from '../models/Challenge';
    import HeroNews from '../models/HeroNews';
    import News from '../models/News';
    // utils
    import {
        LOADER_FETCH_LATEST_TRANSACTIONS,
        LOADER_FETCH_NEWS_LATEST,
        ROUTE_ACCOUNT,
        ROUTE_NEWS,
    } from '@/utils/constants';

    export default {
        name: 'home',
        components: {
            HeadingSection,
            NewsCard,
            WidgetPerformance,
            WidgetWishlist,
            WidgetRangs,
        },
        computed: {
            ...mapState([
                'tours',
                'tourDashboard',
            ]),
            challenges() {
                return Challenge.getAll();
            },
            isFetchingLatestNews() {
                return this.$wait.is(LOADER_FETCH_NEWS_LATEST);
            },
            latestArticles() {
                return News.getLatest();
            },
            heroArticle() {
                return HeroNews.get();
            },
            toAllNews() {
                return { name: ROUTE_NEWS };
            },
            routeAccount() {
                return ROUTE_ACCOUNT;
            },
            untilEverythingIsLoaded() {
                return this.$wait.is(LOADER_FETCH_LATEST_TRANSACTIONS) || this.$wait.is(LOADER_FETCH_NEWS_LATEST);
            },
        },
        watch: {
            untilEverythingIsLoaded(isWaiting) {
                if (!isWaiting && this.tours && !this.tourDashboard) {
                    this.$tours['dashboardTour'].start();
                }
            },
        },
        mounted() {
            News.fetchLatest();
        },
        beforeDestroy() {
            if (this.$tours['dashboardTour'] && this.$tours['dashboardTour'].isRunning) {
                this.$tours['dashboardTour'].stop();
            }
        },
    }
</script>
